import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import English translations
import enCommon from './locales/en/common.json';
import enPages from './locales/en/pages.json';
import enComponents from './locales/en/components.json';
import enErrors from './locales/en/errors.json';
import enModals from './locales/en/modals.json';

// Import German translations (placeholder)
import deCommon from './locales/de/common.json';
import dePages from './locales/de/pages.json';
import deComponents from './locales/de/components.json';
import deErrors from './locales/de/errors.json';
import deModals from './locales/de/modals.json';

// Import French translations (placeholder)
import frCommon from './locales/fr/common.json';
import frPages from './locales/fr/pages.json';
import frComponents from './locales/fr/components.json';
import frErrors from './locales/fr/errors.json';
import frModals from './locales/fr/modals.json';

// Import Dutch translations (placeholder)
import nlCommon from './locales/nl/common.json';
import nlPages from './locales/nl/pages.json';
import nlComponents from './locales/nl/components.json';
import nlErrors from './locales/nl/errors.json';
import nlModals from './locales/nl/modals.json';

// Import Polish translations (placeholder)
import plCommon from './locales/pl/common.json';
import plPages from './locales/pl/pages.json';
import plComponents from './locales/pl/components.json';
import plErrors from './locales/pl/errors.json';
import plModals from './locales/pl/modals.json';

// Import Swedish translations
import svCommon from './locales/sv/common.json';
import svPages from './locales/sv/pages.json';
import svComponents from './locales/sv/components.json';
import svErrors from './locales/sv/errors.json';
import svModals from './locales/sv/modals.json';

// Define supported languages
export const supportedLanguages = ['en', 'de', 'fr', 'nl', 'pl', 'sv', 'dev'] as const;
export type SupportedLanguage = typeof supportedLanguages[number];

// Define namespaces
export const namespaces = ['common', 'pages', 'components', 'errors', 'modals'] as const;
export type Namespace = typeof namespaces[number];

// Function to create developer language resources that show localization keys
const createDeveloperResources = (baseResources: any, namespace: string) => {
  const createKeyMap = (obj: any, prefix = ''): any => {
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (typeof value === 'object' && value !== null) {
        result[key] = createKeyMap(value, fullKey);
      } else {
        result[key] = `${namespace}:${fullKey}`;
      }
    }
    return result;
  };
  return createKeyMap(baseResources);
};

// Resources configuration
const resources = {
  en: {
    common: enCommon,
    pages: enPages,
    components: enComponents,
    errors: enErrors,
    modals: enModals,
  },
  de: {
    common: deCommon,
    pages: dePages,
    components: deComponents,
    errors: deErrors,
    modals: deModals,
  },
  fr: {
    common: frCommon,
    pages: frPages,
    components: frComponents,
    errors: frErrors,
    modals: frModals,
  },
  nl: {
    common: nlCommon,
    pages: nlPages,
    components: nlComponents,
    errors: nlErrors,
    modals: nlModals,
  },
  pl: {
    common: plCommon,
    pages: plPages,
    components: plComponents,
    errors: plErrors,
    modals: plModals,
  },
  sv: {
    common: svCommon,
    pages: svPages,
    components: svComponents,
    errors: svErrors,
    modals: svModals,
  },
  dev: {
    common: createDeveloperResources(enCommon, 'common'),
    pages: createDeveloperResources(enPages, 'pages'),
    components: createDeveloperResources(enComponents, 'components'),
    errors: createDeveloperResources(enErrors, 'errors'),
    modals: createDeveloperResources(enModals, 'modals'),
  },
};

// Language detector configuration optimized for mobile apps
const languageDetectorOptions = {
  // Order of language detection methods
  order: [
    'localStorage',
    'navigator',
    'htmlTag',
    'path',
    'subdomain',
  ],
  
  // Keys for localStorage
  lookupLocalStorage: 'i18nextLng',
  
  // Cache user language
  caches: ['localStorage'],
  
  // Exclude certain languages from detection
  excludeCacheFor: ['cimode'],
  
  // Check for supported languages only
  checkWhitelist: true,
};

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    
    // Default language
    fallbackLng: 'en',
    
    // Supported languages
    supportedLngs: supportedLanguages,
    
    // Default namespace
    defaultNS: 'common',
    
    // Available namespaces
    ns: namespaces,
    
    // Language detector configuration
    detection: languageDetectorOptions,
    
    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    // React configuration
    react: {
      useSuspense: false, // Disable suspense for better mobile performance
    },
    
    // Debug mode (disable in production)
    debug: import.meta.env.DEV,
    
    // Key separator
    keySeparator: '.',
    
    // Namespace separator
    nsSeparator: ':',
    
    // Pluralization
    pluralSeparator: '_',
    
    // Context separator
    contextSeparator: '_',
    
    // Return empty string for missing keys in development
    returnEmptyString: import.meta.env.DEV,
    
    // Return key if translation is missing
    returnNull: false,
    
    // Load all namespaces
    load: 'languageOnly',
    
    // Preload languages
    preload: ['en'],
    
    // Clean code
    cleanCode: true,
  });

export default i18n;
